import Phaser from "phaser";
import type { CharacterGridSpec } from "@/character/grid";
import { registerCharacterSceneAnims } from "@/game/register-character-scene-anims";
import {
  SHADOW_DEPTH_BEHIND,
  SHADOW_DISPLAY_ALPHA,
  SHADOW_SCALE_MULTIPLIER,
} from "@/shadow/constants";

const SHADOW_ANIM_PREFIX = "shadow-";
const SHADOW_SHEET_TEXTURE = "shadow-spritesheet";

export function shadowTexturesEnabled(scene: Phaser.Scene): boolean {
  return (
    scene.textures.exists(SHADOW_SHEET_TEXTURE) ||
    scene.textures.exists("shadow-idle")
  );
}

function ensureShadowSceneAnims(scene: Phaser.Scene): void {
  const grid = scene.registry.get("shadowSpritesheetGrid") as CharacterGridSpec | undefined;
  if (!grid) return;
  registerCharacterSceneAnims(scene, SHADOW_SHEET_TEXTURE, SHADOW_ANIM_PREFIX, grid);
}

function shadowTextureKey(scene: Phaser.Scene): { key: string; frame: number } {
  ensureShadowSceneAnims(scene);
  if (scene.textures.exists(SHADOW_SHEET_TEXTURE)) {
    return { key: SHADOW_SHEET_TEXTURE, frame: 0 };
  }
  if (scene.textures.exists("shadow-idle")) {
    return { key: "shadow-idle", frame: 0 };
  }
  return { key: "player-placeholder", frame: 0 };
}

function applyShadowScale(sprite: Phaser.GameObjects.Sprite, scene: Phaser.Scene): void {
  const grid = scene.registry.get("shadowSpritesheetGrid") as
    | { frameHeight: number }
    | undefined;
  const playerGrid = scene.registry.get("playerSpritesheetGrid") as
    | { frameHeight: number }
    | undefined;
  const gridRef = grid ?? playerGrid;
  const targetHeight =
    (scene.registry.get("playerDisplayHeightPx") as number | undefined) ?? 96;
  const baseScale = gridRef ? targetHeight / gridRef.frameHeight : 2;
  sprite.setScale(baseScale * SHADOW_SCALE_MULTIPLIER);
}

function mapPlayerAnimToShadow(playerAnimKey: string | undefined): string | null {
  if (!playerAnimKey) return null;
  if (playerAnimKey === "player-idle") return `${SHADOW_ANIM_PREFIX}idle`;
  if (playerAnimKey === "player-run") return `${SHADOW_ANIM_PREFIX}run`;
  if (playerAnimKey === "player-attack") return `${SHADOW_ANIM_PREFIX}attack`;
  return null;
}

function ensureShadowSpritesheetTexture(shadow: Phaser.GameObjects.Sprite, scene: Phaser.Scene): void {
  if (!scene.textures.exists(SHADOW_SHEET_TEXTURE)) return;
  if (shadow.texture.key === SHADOW_SHEET_TEXTURE) return;
  shadow.setTexture(SHADOW_SHEET_TEXTURE, 0);
}

function playShadowAnim(
  shadow: Phaser.GameObjects.Sprite,
  scene: Phaser.Scene,
  shadowKey: string,
  restart = false,
): void {
  if (!scene.anims.exists(shadowKey)) return;
  ensureShadowSpritesheetTexture(shadow, scene);
  const cur = shadow.anims.currentAnim?.key;
  if (cur === shadowKey && !restart) return;
  const loop = shadowKey !== `${SHADOW_ANIM_PREFIX}attack`;
  shadow.play(shadowKey, loop && !restart);
}

export type PlayerShadowCompanion = {
  sprite: Phaser.GameObjects.Sprite;
  syncFromPlayer: (player: Phaser.GameObjects.Sprite) => void;
  destroy: () => void;
};

export function attachPlayerShadowCompanion(
  scene: Phaser.Scene,
  player: Phaser.GameObjects.Sprite,
): PlayerShadowCompanion | null {
  if (!shadowTexturesEnabled(scene)) return null;
  ensureShadowSceneAnims(scene);
  const texture = shadowTextureKey(scene);
  const shadow = scene.add.sprite(player.x, player.y, texture.key, texture.frame);
  shadow.setOrigin(0.5, 1);
  shadow.setAlpha(SHADOW_DISPLAY_ALPHA);
  applyShadowScale(shadow, scene);
  const idleKey = `${SHADOW_ANIM_PREFIX}idle`;
  if (scene.anims.exists(idleKey)) {
    shadow.play(idleKey);
  }
  shadow.setDepth(player.depth - SHADOW_DEPTH_BEHIND);

  const syncAnimFromKey = (playerAnimKey: string | undefined, restart = false) => {
    const shadowKey = mapPlayerAnimToShadow(playerAnimKey);
    if (!shadowKey) return;
    playShadowAnim(shadow, scene, shadowKey, restart);
  };

  const syncFromPlayer = (p: Phaser.GameObjects.Sprite) => {
    if (!shadow.active) return;
    shadow.setPosition(p.x, p.y);
    shadow.setFlipX(p.flipX);
    shadow.setVisible(p.visible);
    shadow.setDepth(p.depth - SHADOW_DEPTH_BEHIND);
    const playerAnimKey = p.anims.currentAnim?.key;
    // Avoid restarting attack every frame (update loop calls syncFromPlayer).
    if (playerAnimKey === "player-attack") return;
    syncAnimFromKey(playerAnimKey);
  };

  const onPlayerAnimStart = (animation: Phaser.Animations.Animation) => {
    syncAnimFromKey(animation.key, animation.key === "player-attack");
  };
  player.on("animationstart", onPlayerAnimStart);

  syncFromPlayer(player);

  return {
    sprite: shadow,
    syncFromPlayer,
    destroy: () => {
      player.off("animationstart", onPlayerAnimStart);
      shadow.destroy();
    },
  };
}
